import moment from "moment";
import React from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { DateRangePicker } from "src/components/forms/picker";
import { PdSelect, tPdSelectOption } from "src/components/forms/select";
import { PdStack } from "src/components/layout";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { useAppDispatch } from "src/redux";
import { tModalComponentProps } from "src/redux/modals/types";
import { exportOrders } from "src/redux/orders/actions";
import { orderStatusOptions, reportDateRanges } from "src/util/comboBoxData";
import { useIsMobile } from "../ui";

type tOrderParams = { orderStatus?: string; start?: string; end?: string };

const ExportOrdersModal: React.FC<tModalComponentProps<unknown, tOrderParams>> = ({ onResolve, onClose }) => {
  const [orderStatus, setOrderStatus] = React.useState(orderStatusOptions[0]);
  const [dateRange, setDateRange] = React.useState(reportDateRanges[0]);
  const [customStartDate, setCustomStartDate] = React.useState<Date | null>(new Date());
  const [customEndDate, setCustomEndDate] = React.useState<Date | null>(null);
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();

  const isCustom = dateRange.value === "Custom";
  const isFormValid = isCustom ? customStartDate && customEndDate : true;

  const handleOnClose = () => {
    onClose();
  };

  const submit = () => {
    const params: tOrderParams = {};
    if (dateRange.value === "All Time") {
      params.start = String(moment().subtract(1, "year").startOf("day").valueOf());
      params.end = String(moment().endOf("day").valueOf());
    } else if (dateRange.value === "Last 30 Days") {
      params.start = String(moment().subtract(30, "days").startOf("day").valueOf());
      params.end = String(moment().endOf("day").valueOf());
    } else if (dateRange.value === "Last 60 Days") {
      params.start = String(moment().subtract(60, "days").startOf("day").valueOf());
      params.end = String(moment().endOf("day").valueOf());
    } else if (dateRange.value === "Custom") {
      params.start = String(moment(customStartDate).startOf("day").valueOf());
      params.end = String(moment(customEndDate).endOf("day").valueOf());
    }

    if (orderStatus.value !== "All") {
      params.orderStatus = orderStatus.value;
    }

    onResolve(params);
    dispatch(exportOrders(params));
  };

  const handleOnSelectOrderStatus = (option: tPdSelectOption | null) => {
    if (option) setOrderStatus(option);
  };

  const handleOnSelectDateRange = (option: tPdSelectOption | null) => {
    if (option) setDateRange(option);
  };

  const handeOnChangeCustomDate = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  return (
    <PdModal style={styles.modal} onClose={handleOnClose}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <PdModalTitle>Export Orders</PdModalTitle>
        <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} style={styles.modalFields}>
          <View style={styles.zIndex1}>
            <PdFormLabel>Orders to export</PdFormLabel>
            <PdSelect
              value={orderStatus}
              onChange={handleOnSelectOrderStatus}
              options={orderStatusOptions}
              isClearable={false}
            />
          </View>
          <View>
            <PdFormLabel>Date range</PdFormLabel>
            <PdSelect
              value={dateRange}
              onChange={handleOnSelectDateRange}
              options={reportDateRanges}
              isClearable={false}
            />
          </View>
        </PdStack>

        {isCustom && (
          <View>
            <PdFormLabel>Custom date</PdFormLabel>
            <DateRangePicker
              selected={customStartDate}
              startDate={customStartDate}
              endDate={customEndDate}
              onChange={handeOnChangeCustomDate}
            />
          </View>
        )}
        <PdStack
          direction={isMobile ? "vertical" : "horizontal"}
          isReversed={isMobile}
          layout={[1, 1]}
          gap={3}
          style={styles.modalButtonGroup}
        >
          <PdButton colorScheme="purple" onPress={handleOnClose} variant="outline">
            <PdButtonLabel>Cancel</PdButtonLabel>
          </PdButton>
          <PdButton onPress={submit} isDisabled={!isFormValid}>
            <PdButtonLabel>Submit</PdButtonLabel>
          </PdButton>
        </PdStack>
      </ScrollView>
    </PdModal>
  );
};

const useExportOrdersModal = (): tUseModal<unknown, tOrderParams> => {
  return useModal<unknown, tOrderParams>(ExportOrdersModal);
};

export default useExportOrdersModal;

const styles = StyleSheet.create({
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  modalButtonGroup: {
    paddingTop: 14 * kStyleConst.kSpacing,
    width: "100%",
  },
  modalFields: {
    marginBottom: 4 * kStyleConst.kSpacing,
    marginTop: 8 * kStyleConst.kSpacing,
    width: "100%",
    zIndex: 1,
  },
  zIndex1: {
    zIndex: 1,
  },
});
